// various generic helper functions for AG-grid parsers, formatters, renderers, etc.

// getters / setters --> for altering WHERE a value comes from, ie: to/from a nested object, etc.
// formatters / parsers --> altering the value itself  (not the HTML around the value)
// renderers / editors --> the widget/component used to display (renderer) or to capture input (editor)

import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';

import { hasValueOrZero, toNumberOrNull, HumanizeDurationFromIsoHelper, FormatDateHelper } from './filters-helpers';



const getEventPath = function(event) {
    // pass in a standard DOM Event (like a rowDragEvent.event) - this will return an array of DOM elements that you can use
    // why?  because event.path is a Chrome thing, and Firefox uses event.composedPath()
    // this returns an empty array if the browswer does not support event.path or event.composedPath()
    //console.log('from getEventPath - event is: ', event);
    // NOTE - event.path was deprecated by Chrome, just use composedPath() now
    //let path = event.path || (event.composedPath && event.composedPath());
    //let path = event.composedPath();  // doesn't work... not really what you want
    let path = [];
    let element = event.target;
    while (element) {
        path.push(element);
        element = element.parentElement;  // keep walking up
    }
    if (path) {
        return path;
    } else {
        return [];
    }
}

const agStringParser = function(params) {
    // forces newValue to be a string, empty string '' if newValue is falsey
    return params.newValue ? params.newValue : '';
}

const agStringFormatter = function(params) {
    // forces params.value to be formatted as a string
    return params.value.toString();
}

const agNumberParser = function(params) {  // use on editable cells that should be numbers
    return toNumberOrNull(params.newValue);  // returns null if value is null or undefined, otherwise a finite number
};  // this is used in both the QuoteSheetBuilder AND the QuoteSheetReply (no longer as of Oct 3 2022, but uses toNumberOrNull still)

const agNumberFormatter = function(params) {  
    // takes a number, formats using localized format (.toLocaleString()) - works with integers or decimals
    // uses toNumberOrNull to make sure its a (finite) number first, or returns null if not a number
    let value = toNumberOrNull(params.value);
    return hasValueOrZero(value) ? value.toLocaleString() : value;  // just pass through value if you don't have it (toNumberOrNull will output null for any non-number input)
}

const agIsoDurationFormatter = function(params) {  // takes value as duration ISO string (P5W2D) and outputs as '5 weeks, 2 days'
    return HumanizeDurationFromIsoHelper(params.value);
};

const agDateFormatter = function(params) { // use as a valueFormatter for Dates (no time)
    // use this instead of new Date() -- because JS Date() when parsing just the date ('2022-09-22') will screw up the timezones
    return FormatDateHelper(params.value);
}

const agNumberFixedDecimalParser = function(rawValue, decimalPlaces) {
    // return either:
    //      null, or
    //      Number with truncated/rounded? decimalPlaces

    const fixedDecimalDigits = decimalPlaces ?? 4;  // maybe set this default somewhere else???  some project-wide settings?

    // you should NOT need this when just DISPLAYING already-entered values -- because this can change the value... by rounding/truncating
    // for displaying, you can/should use something simpler, like just toNumberOrNull(), then use formatter below 
    // IMPORTANT - make sure max decimal digits match your backend, and your formatter below
    // OLD VERSION return toNumberOrNull(params.newValue);  // returns null if value is null or undefined, otherwise a finite number
    // NEW VERSION -- we will truncate to a specific fixed decimal digits -- also uses same code from toNumberOrNull, but keeping here, for easier encapsulation
    // coerce value to a Number if possible, otherwise return null
    let rawNumber;  // undefined for now... but if we have an actual Number, set it here, BEFORE we call .toFixed()
    if (isNil(rawValue) || rawValue === '') {  // checks for null or undefined or empty string '' - why check empty string here?  we need to be able to clear a cell to no value
        return null;
    } else if (Number.isFinite(rawValue)) {  // if its already a number, just use it as is
        rawNumber = rawValue;
        //return rawValue;
    } else {  // if here, its likely a string, so coerce it, check result, and return accordingly
        let numberOrNaN = toNumber(rawValue);  // lodash toNumber() coerces things to a finite number, or NaN.  so must check if NaN afterwards
        if (Number.isNaN(numberOrNaN)) {  // is it for sure NaN?  then return null
            return null;
        } else {  // otherwise we have a number here
            rawNumber = numberOrNaN;
        }
        // return Number.isNaN(numberOrNaN) ? null : numberOrNaN;  // return null instead of NaN... or the number
        // previously used lodash toFinite() but that coerces things to zero instead of NaN
    }
    // now we definitely have a number... need to truncate/round to number of digits... 
    // we will call .toFixed() which CONVERTS TO A STRING, then Number.parseFloat(fixedNumber); to go back to a number
    // NOTE - your serializer may want a string anyways (but seems it can take numbers) so you MAY want to represent things here as strings, not numbers (floats)
    //      - ESPECIALLY IF YOU DO ANY MATH HERE!
    const fixedNumber = rawNumber.toFixed(fixedDecimalDigits);
    return Number.parseFloat(fixedNumber);  // back to a Number and return it
};

export {
    agStringParser,
    agStringFormatter,
    agNumberParser,
    agIsoDurationFormatter,
    agNumberFormatter,
    getEventPath,
    agDateFormatter,
    agNumberFixedDecimalParser,
}